<script setup lang="ts">
import { computed } from 'vue';
import BaseButton from '@/components/base/BaseButton.vue';

import { getTranslation, getLabel } from '@/utils/data-display-utils';
import { useCommunityStore } from '@/store/communityStore';
import { constant } from '@/constant';
import { storeToRefs } from 'pinia';

// PROPS

const communityStore = useCommunityStore();
const { community, customPageLoaded } = storeToRefs(communityStore);
const aboutPage = community.value.customPages.find((p) => p.type === constant.Community.CustomPage.About);
const communityTitle = computed(() =>
  getTranslation(communityStore.community, 'title', communityStore.community?.languageEnabled),
);
</script>

<template>
  <div class="about-page" :aria-labelledby="'about-page-title'">
    <div class="about-page__content">
      <h1 id="about-page-title" class="about-page__title">
        {{ getLabel('about-us', communityStore.community?.languageEnabled) }}
      </h1>

      <section
        class="about-page__content-consultation"
        v-if="customPageLoaded && aboutPage"
        :aria-labelledby="'about-community-title'"
      >
        <h2 id="about-community-title" class="about-page__section-title">
          {{ getLabel('about.about-us', communityStore.community?.languageEnabled) }} {{ communityTitle }}
        </h2>
        <div
          class="ck-rich-text"
          v-html="getTranslation(aboutPage, 'content', communityStore.community?.languageEnabled)"
        ></div>
      </section>

      <q-separator aria-hidden="true" />

      <section class="about-page__content-cocoriko" :aria-labelledby="'about-cocoriko-title'">
        <h2 id="about-cocoriko-title" class="about-page__section-title">
          {{ getLabel('about.about-us', communityStore.community?.languageEnabled) }} Cocoriko
        </h2>
        <BaseButton isOrange class="contact" href="mailto:info@cocoriko.org">{{
          getLabel('about.contact', communityStore.community?.languageEnabled)
        }}</BaseButton>
        <div class="comments">
          {{ getLabel('about.questions-comments', communityStore.community?.languageEnabled) }}
          <a href="mailto:info@cocoriko.org">info@cocoriko.org</a>
        </div>

        <div class="about">
          {{
            getLabel('about.cocoriko-platform', communityStore.community?.languageEnabled, {
              named: {
                client: communityTitle,
              },
            })
          }}
        </div>
        <BaseButton primary class="about-button" href="https://www.cocoriko.org/"
          >{{ getLabel('about.about-us', communityStore.community?.languageEnabled) }} Cocoriko</BaseButton
        >
      </section>
    </div>
  </div>
</template>

<style lang="scss">
.about-page {
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: $space-xl;
  font-size: $body-text;

  &__title {
    font-size: $medium-title;
    line-height: 1.2;
    margin: 0 0 $space-lg;
  }

  &__section-title {
    font-size: $header;
    line-height: 1.2;
    margin: 0 0 $space-md;
  }

  &__content {
    max-width: 720px;

    &-consultation {
      margin-bottom: $space-lg;
    }

    &-cocoriko {
      margin-top: $space-lg;

      .comments {
        padding: $space-sm 0;
      }

      .about {
        padding: $space-sm 0;
      }
    }
  }

  a:focus-visible,
  .q-btn.base-button:focus-visible {
    outline: 3px solid $color-primary-darken;
    outline-offset: 3px;
    border-radius: 8px;
    box-shadow: 0 0 0 4px rgba($color-primary, 0.2) !important;
  }
}

@media screen and (max-width: $breakpoint-sm) {
  .about-page {
    padding: $space-md;
  }
}
</style>
